import Bugsnag, { type BrowserConfig } from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import User from 'classes/user';
import EventBus from 'components/event-bus';
import { enableRouting } from 'components/router';
import ajax from 'controllers/ajax';
import analytics from 'controllers/analytics';
import auth from 'controllers/auth';
import experiment from 'controllers/experiment';
import storage from 'controllers/storage';
import Support from 'controllers/support';
import upload from 'controllers/upload';
import * as DB from 'interfaces/database';
import LogRocket from 'logrocket';
import getBugsnagBreadcrumbTypes from 'services/bugsnag-breadcrumbs';
import {
	configureBugsnagPerformance,
	startBugsnagSpan,
} from 'services/bugsnag-performance';
import { BUGSNAG_REDACTED_KEYS } from 'services/bugsnag-privacy';
import CheckConnection from 'services/check-connection';
import * as DialogService from 'services/dialog';
import { ERRORS_OFFLINE } from 'settings/errors';
import URL_PARAMETERS from 'settings/url-parameters';
import store, {
	AppDataModule,
	AppStateModule,
	ChannelsModule,
	ConfigModule,
	DiscountModule,
	UserDataModule,
	UserModule,
} from 'store';
import getActiveLabel from 'tools/active-label';
import detectUpload from 'tools/detect-upload';
import getCookie from 'tools/get-cookie';
import parseUrl from 'tools/parse-url';
import setCookie from 'tools/set-cookie';
import _ from 'underscore';
import { getErrorMetadata } from 'utils/annotate-error';
import isServiceWorkerRegistrationNoise from 'utils/is-service-worker-registration-noise';
import reportError from 'utils/report-error';
import sanitizeBreadcrumbUrls from 'utils/sanitize-breadcrumb-urls';
import sanitizeUrl from 'utils/sanitize-url';
import type { App } from 'vue';
import type { Router } from 'vue-router';

const BUGSNAG_API_KEY = '7108d26410396261866519684a732fd5';
const BUGSNAG_ENABLED_RELEASE_STAGES: Window['glStack'][] = [
	'local',
	'staging',
	'live',
];

function sanitizeTelemetryUrl(value: string): string {
	return sanitizeUrl(
		value,
		{
			collapsePathPrefixes: [ConfigModule.projectImageBaseUrl],
		},
	);
}

function fetch(): Promise<void> {
	// Fetch data
	if (!AppStateModule.online) {
		// Not online and no data locally available, so reject with 'offline' reason
		return Promise.reject(new Error(ERRORS_OFFLINE));
	}

	return AppDataModule
		.fetch({
			dataToExclude: [
				'flexgroups',
				'model2ds',
				'model3ds',
				'pricing',
				'region_offerings',
				'offeringframes',
				'offeringframeimages',
				'offeringframetemplates',
				'offerings',
				'offeringoptions',
				'offeringoptionvalues',
				'offeringoptionvalue_offerings',
				'productcategories',
				'translations_offerings',
			],
		})
		.catch((err) => {
			if (!AppStateModule.online) {
				// Not online and no data locally available, so reject with 'offline' reason
				throw new Error(ERRORS_OFFLINE);
			}

			// App is online, but fetching from database failed, so reject
			throw err;
		});
}

function retrySetupAfterOfflineError(retry: () => Promise<void>): Promise<void> {
	return new Promise((res, rej) => {
		const closeError = DialogService.openErrorDialog({
			header: {
				hasCloseButton: false,
				title: window.App.router.$t('dialogHeaderOffline'),
			},
			body: {
				content: window.App.router.$t('dialogTextOffline'),
			},
			footer: {
				buttons: [
					{
						id: 'accept',
						text: window.App.router.$t('dialogButtonOfflineOk'),
						click: () => {
							CheckConnection()
								.catch(() => {
									// Swallow error: no action required
								})
								.finally(() => {
									retry().then(
										res,
										rej,
									);
								});
							closeError();
						},
					},
				],
			},
		});
	});
}

function handleSetupError(
	error: unknown,
	retry: () => Promise<void>,
): Promise<void> {
	if (error instanceof Error
		&& error.message == ERRORS_OFFLINE
	) {
		return retrySetupAfterOfflineError(retry);
	}

	throw error;
}

async function setup(performFetch = true): Promise<void> {
	try {
		const parsedUrl = parseUrl(window.location.href);
		const urlVars = parsedUrl.parameters;

		if (URL_PARAMETERS.voucherCode in urlVars) {
			storage.set(
				'vc',
				urlVars[URL_PARAMETERS.voucherCode],
			);
		}

		if (URL_PARAMETERS.priceMultiplier in urlVars) {
			const multiplier = urlVars[URL_PARAMETERS.priceMultiplier] as string;

			if (multiplier) {
				AppStateModule.setPricingMultiplier(parseFloat(multiplier));
			}

			storage.set(
				URL_PARAMETERS.priceMultiplier,
				multiplier,
			);

			await window.App.router.removeURLParameter(URL_PARAMETERS.priceMultiplier);
		} else {
			const multiplier = storage.get(URL_PARAMETERS.priceMultiplier);

			if (multiplier) {
				AppStateModule.setPricingMultiplier(parseFloat(multiplier));
			}
		}

		if (performFetch) {
			await fetch();
		}

		if (ConfigModule['LogRocket.id']
			&& ConfigModule['LogRocket.enabled']
		) {
			/*
			 * We accept the incoming URL for LogRocket recording if:
			 * * there is no filter defined (accept all incoming requests)
			 * * OR
			 * * the incoming URL matches the defined regex filter
			 */
			const acceptUrl = !ConfigModule['LogRocket.filter.url']
				|| new RegExp(
					ConfigModule['LogRocket.filter.url'],
					'g',
				).exec(window.location.href);

			if (acceptUrl) {
				/*
				 * Same URL policy as BugSnag: Creator's access_code, user_token and
				 * email arrive as query parameters, so neither the recorded page URLs
				 * nor the captured requests may carry a query string.
				 */
				LogRocket.init(
					ConfigModule['LogRocket.id'],
					{
						browser: {
							urlSanitizer: sanitizeTelemetryUrl,
						},
						network: {
							requestSanitizer: (request) => ({
								...request,
								referrer: (
									request.referrer
									&& sanitizeTelemetryUrl(request.referrer)
								),
								url: sanitizeTelemetryUrl(request.url),
							}),
						},
					},
				);
			}
		}

		if (ConfigModule['debug.remotejs']) {
			(function () {
				const s = document.createElement('script');
				s.src = 'https://remotejs.com/agent/agent.js';
				s.setAttribute(
					'data-consolejs-channel',
					ConfigModule['debug.remotejs'],
				);
				document.head.appendChild(s);
			}());
		}

		// Disable uploads for devices that have no such functionality
		if (!detectUpload()) {
			ChannelsModule.removeModel('upload');
		}

		// Get user country
		let countryid = null;

		if (URL_PARAMETERS.country in urlVars) {
			const countryModel = AppDataModule.findCountryWhere({
				iso: urlVars[URL_PARAMETERS.country],
			});
			countryid = countryModel ? countryModel.id : null;
		} else if (
			URL_PARAMETERS.countryId in urlVars
			&& parseInt(
				urlVars[URL_PARAMETERS.countryId],
				10,
			) > 0
		) {
			// Backwards compatability - countryid parameter has been replaced with country
			countryid = parseInt(
				urlVars[URL_PARAMETERS.countryId],
				10,
			);
		}
		if (!countryid
			&& AppDataModule.countries.length == 1
		) {
			countryid = AppDataModule.countries[0].id;
		}

		const countryModel = countryid
			? AppDataModule.getCountry(countryid)
			: undefined;
		const regionid = countryModel
			? countryModel.regionid
			: null;
		let currency = null;

		if (regionid
			&& AppDataModule.findRegionCurrencyLink({
				regionid,
			}).length == 1
		) {
			const regionCurrencyModel = AppDataModule.findRegionCurrencyLinkWhere({
				regionid,
			});

			if (regionCurrencyModel) {
				currency = regionCurrencyModel.currencyid;
			}
		} else if (countryid
			&& AppDataModule.getCountry(countryid)) {
			const cModel = AppDataModule.getCountry(countryid);

			if (cModel) {
				({ currency } = cModel);
			}
		} else if (
			URL_PARAMETERS.currency in urlVars
			&& urlVars[URL_PARAMETERS.currency]
		) {
			const currencyModel = AppDataModule.getCurrency(urlVars[URL_PARAMETERS.currency])
				?? AppDataModule.getCurrencyByISO(urlVars[URL_PARAMETERS.currency]);

			if (currencyModel) {
				currency = currencyModel.id;
			}
		} else if (AppDataModule.defaultCurrency) {
			const currencyModel = AppDataModule.defaultCurrency;

			if (currencyModel) {
				currency = currencyModel.id;
			}
		}

		// Check for prefilled voucher codes in the URL (do not overwrite if already set)
		const discountVoucherModel = DiscountModule.getVoucher;

		if (!discountVoucherModel.code) {
			if (URL_PARAMETERS.voucherCode in urlVars) {
				// Voucher code found, add to discount state model
				DiscountModule.setVoucher({
					code: urlVars[URL_PARAMETERS.voucherCode],
				});
			} else {
				const voucherCode = storage.get(URL_PARAMETERS.voucherCode);

				if (voucherCode) {
					// Voucher code found, add to discount state model
					DiscountModule.setVoucher({
						code: voucherCode,
					});
				}
			}
		}

		const userAttributes: Partial<DB.UserModel> = {};

		if (countryid) {
			userAttributes.countryid = countryid;
		}
		if (currency) {
			userAttributes.currency = currency;
		}
		if (!UserModule.language) {
			userAttributes.language = window.locale;
		}
		if (!UserModule.affiliateid) {
			userAttributes.affiliateid = window.affiliateID;
		}

		if (UserModule.id) {
			await UserModule
				.put({
					awaitProductCategoriesData: false,
					data: userAttributes,
				})
				.catch(() => undefined);
			// Note: On failure swallow error: no action required
		}

		/*
		 * Do not block routing on the product catalogue — it is the slowest
		 * boot request and the shelf renders an inline loading state and fills
		 * in reactively when it lands (views/selector-product-items). Offline
		 * failures keep the retry dialog; other failures degrade to an empty
		 * category list and a Bugsnag warning instead of failing the launch,
		 * since the rest of the app is already usable at that point.
		 */
		const fetchProductCategoriesDataWithOfflineRetry = (): Promise<void> => (
			UserModule
				.setAndWaitForProductCategoriesData(userAttributes)
				.catch((error) => handleSetupError(
					error,
					fetchProductCategoriesDataWithOfflineRetry,
				))
		);

		fetchProductCategoriesDataWithOfflineRetry()
			.catch((error) => {
				reportError(
					error,
					'warning',
				);
			});
	} catch (error) {
		await handleSetupError(
			error,
			() => setup(),
		);
	}
}

function fetchAppDataAndSetup(): Promise<void> {
	return fetch()
		.then(() => setup(false))
		.catch((error: unknown) => handleSetupError(
			error,
			fetchAppDataAndSetup,
		));
}

async function setupUserAndAppData(performFetch: boolean): Promise<void> {
	if (!performFetch) {
		await User.setup(
			false,
			false,
		);
		await setup(false);
		return;
	}

	if (!AppStateModule.online) {
		await User.setup(
			false,
			false,
		);
		await fetchAppDataAndSetup();
		return;
	}

	/*
	 * `/api/app/user` owns the `x-app-data-version` response header, which the
	 * Ajax controller copies to `window.appDataVersion`. Keep `/start` behind
	 * user setup in every environment so staging exercises the same boot order
	 * before the build is promoted to live.
	 */
	await User.setup(
		false,
		false,
	);
	return fetchAppDataAndSetup();
}

async function checkToken(): Promise<void> {
	const parsedUrl = parseUrl(window.location.href);
	const urlVars = parsedUrl.parameters;

	if (
		urlVars[URL_PARAMETERS.accessCode]
		|| urlVars[URL_PARAMETERS.userToken]
	) {
		const loginData: Record<string, string> = {};

		if (urlVars[URL_PARAMETERS.accessCode]) {
			await window.App.router.removeURLParameter(URL_PARAMETERS.accessCode);
			loginData.access_code = urlVars[URL_PARAMETERS.accessCode];
		} else if (urlVars[URL_PARAMETERS.userToken]) {
			await window.App.router
				.removeURLParameter(URL_PARAMETERS.userToken)
				.then(() => window.App.router.setLoading(true));
			loginData.userAccessToken = urlVars[URL_PARAMETERS.userToken];
		}

		return auth.login(
			loginData,
			false,
		);
	}

	return Promise.resolve();
}

/**
 * The BugSnag project and release metadata shared by the error and performance
 * SDKs. A function rather than a constant so it is read at boot time, alongside
 * getActiveLabel(), which depends on the live document.
 *
 * @returns The fields both `Bugsnag.start` and the performance SDK need.
 */
function bugsnagReleaseConfig(): NonNullableProperties<Pick<BrowserConfig, 'apiKey' | 'appVersion' | 'enabledReleaseStages' | 'releaseStage'>> {
	return {
		apiKey: BUGSNAG_API_KEY,
		appVersion: VERSION,
		enabledReleaseStages: BUGSNAG_ENABLED_RELEASE_STAGES,
		releaseStage: window.glStack,
	};
}

export function configureBugsnag(appInstance: App): void {
	// Setup Bugsnag for error logging
	window.glBugsnagClient = Bugsnag.start({
		...bugsnagReleaseConfig(),
		appType: (
			getActiveLabel()
			|| undefined
		),
		collectUserIp: false,
		enabledBreadcrumbTypes: getBugsnagBreadcrumbTypes(),
		onBreadcrumb: (breadcrumb) => sanitizeBreadcrumbUrls(
			breadcrumb,
			{
				collapsePathPrefixes: [ConfigModule.projectImageBaseUrl],
			},
		),
		onError: (report) => {
			if (isServiceWorkerRegistrationNoise(report)) {
				return false;
			}

			/*
			 * BugSnag's own request plugin sets request.url to the raw href before
			 * this callback runs, and Creator's incoming URL carries access_code,
			 * user_token and email until they are stripped from the address bar.
			 */
			if (report.request.url) {
				report.request.url = sanitizeTelemetryUrl(report.request.url);
			}

			if (LogRocket?.sessionURL) {
				report.addMetadata(
					'LogRocket',
					{
						sessionURL: LogRocket.sessionURL,
					},
				);
			}

			if (UserModule.id) {
				report.setUser(UserModule.id.toString());
			}

			/*
			 * Surface context attached at throw/reject sites via annotateError().
			 * Most of our errors bubble unhandled (no notify() call site), so the
			 * failing font/photo id would otherwise be lost — this is the only
			 * place to forward it onto the event.
			 */
			const attached = getErrorMetadata(report.originalError);

			if (attached) {
				Object.keys(attached).forEach((section) => {
					report.addMetadata(
						section,
						attached[section],
					);
				});
			}
		},
		plugins: [new BugsnagPluginVue()],
		redactedKeys: BUGSNAG_REDACTED_KEYS,
	});
	const bugsnagVue = Bugsnag.getPlugin('vue');

	if (bugsnagVue) appInstance.use(bugsnagVue);
}

/**
 * Starts the performance SDK after error monitoring and Vue Router are ready,
 * reusing the same project and release metadata.
 *
 * Excluded stages return before starting it: `enabledReleaseStages` only drops
 * spans once they have been created, so starting there would build spans and
 * fetch a sampling probability for telemetry that is thrown away. The SDK still
 * patches fetch/XHR at import; moving the import behind this guard would delay
 * that patching past the requests the page-load spans exist to measure.
 *
 * @param router - Creator's configured Vue {@link Router}.
 */
export function configurePerformanceMonitoring(router: Router): void {
	if (!BUGSNAG_ENABLED_RELEASE_STAGES.includes(window.glStack)) {
		return;
	}

	configureBugsnagPerformance({
		...bugsnagReleaseConfig(),
		getProjectImageBaseUrl: () => ConfigModule.projectImageBaseUrl,
		router,
		serviceName: (
			getActiveLabel()
			|| 'creator'
		),
	});
}

export function launch(performFetch = true): Promise<void> {
	const launchTime = new Date().getTime();

	// Save affiliateid cookie
	if (storage.enabled.cookie) {
		if (window.affiliateID) {
			setCookie(
				'aid',
				window.affiliateID,
			);
		} else if (getCookie('aid')) {
			const cookieValue = getCookie('aid');

			if (cookieValue) {
				window.affiliateID = parseInt(
					cookieValue,
					10,
				);
			}
		}

		if (window.shopID) {
			storage.set(
				'shopID',
				window.shopID,
			);
		} else if (storage.get('shopID')) {
			window.shopID = parseInt(
				storage.get('shopID'),
				10,
			);
		}
	}

	// Save locale cookie
	if (window.locale) {
		storage.set(
			'locale',
			window.locale,
		);
	}

	// Check for existing anonymous user id
	const deviceId = storage.get('deviceId');

	if (deviceId) {
		// This device already has an id, so overwrite the value in the store
		UserDataModule.setDeviceId(deviceId);
	} else {
		/*
		 * Device does not have id yet
		 * Save the new value to the local storage so we can retrieve it in future sessions
		 */
		storage.set(
			'deviceId',
			UserDataModule.getDeviceId,
		);
	}

	// Check if required functionality is available in browser
	if (
		!storage.enabled.cookie
		&& !storage.enabled.data
	) {
		/*
		 * No cookies, and no local storage available
		 * Show error message and abort app
		 */
		DialogService.openErrorDialog({
			body: {
				content: window.App.router.$t('cookiesDisabled'),
			},
			footer: {
				buttons: undefined,
			},
		});

		return Promise.reject();
	}

	// Set online status
	const setStatus = navigator.onLine
		? AppStateModule.setOnline
		: AppStateModule.setOffline;
	const launchSpan = startBugsnagSpan('creator.launch');

	launchSpan?.setAttribute(
		'creator.launch.data_fetch',
		performFetch,
	);

	return setStatus()
		.then(() => {
			/*
			 * Enable autoSave
			 * Note: This must be done after setOnline (to prevent AppStateModule.save from triggering in AppStateModule.setOnline)
			 */
			AppStateModule.enableAutoSave();
		})
		.then(() => checkToken())
		// Setup user and app data
		.then(() => setupUserAndAppData(performFetch))
		.then(() => User.validate())
		// Setup analytics tracking
		.then(() => analytics.setup())
		/*
		 * Setup experiment flags: needs to be done after analytics setup
		 * so that initial flag values can be send to analytics controller
		 */
		.then(() => experiment.init())
		.then(() => {
			// Send user traits to analytics providers
			analytics.setUserProperties();

			// Setup support integrations
			Support.setup();

			// Setup upload controller
			upload.init();

			// Setup auth controller
			auth.init();

			// Clean storage data and files that are no longer used
			storage.garbageCleaner();

			// Close loader dialog
			DialogService.closeLoaderDialog();

			// Track performance from loading of HTML to launch
			analytics.trackPerformance(
				'Launch time',
				launchTime - window.startTime,
			);

			analytics.trackEvent(
				'App started',
				{
					version: VERSION,
					platform: window.glPlatform,
					path: window.location.pathname,
				},
			);

			/**
			 * Indicate that the app has finalized its launch
			 * and the routing can be enabled
			 */
			enableRouting();

			// Sync external shopping basket with ours after routing is enabled
			void User.syncExternalCart();

			// Add auto save interval
			const throttle = _.throttle(
				() => {
					AppStateModule.save()
						.catch((e) => {
							// Swallow error: no action required
							reportError(
								e,
								'warning',
							);
						});
				},
				5000,
				{
					leading: false,
				},
			);
			store.watch(
				(_state, getters) => getters['productstate/getSaved'],
				(saved) => {
					if (saved === true) {
						throttle.cancel();
					} else if (saved === false && AppStateModule.autoSave) {
						throttle();
					}
				},
			);

			// Check if we have the latest app data on app resume
			EventBus.on(
				'app:resume',
				() => {
					ajax
						.request(
							{
								url: '/webhook/ping',
								method: 'get',
							},
							{
								auth: false,
								debug: {
									log: false,
								},
							},
						)
						.then((resp) => {
							if (resp
							&& resp.data
							&& resp.data['data-version']
							&& resp.data['data-version'] != window.appDataVersion
							) {
								window.dataUpdate = true;
							}
						})
						.catch(() => {
						// Do nothing
						});
				},
			);

			launchSpan?.setAttribute(
				'creator.launch.outcome',
				'success',
			);
			launchSpan?.end();

			return undefined;
		})
		.catch((error) => {
			launchSpan?.setAttribute(
				'creator.launch.outcome',
				'failure',
			);
			launchSpan?.end();

			// Close loader dialog
			DialogService.closeLoaderDialog();

			/*
			 * Data could not be loaded
			 * Show error dialog
			 */
			const closeError = DialogService.openErrorDialog({
				body: {
					content: window.App.router.$t('dialogTextLaunchError'),
				},
				footer: {
					buttons: [
						{
							id: 'accept',
							text: window.App.router.$t('dialogButtonErrorOk'),
							click: () => {
								window.location.reload();
								closeError();
							},
						},
					],
				},
			});

			reportError(
				error,
				'error',
			);

			throw error;
		});
}
